<template>
    <v-tabs-items class="transparent" v-model="tab" align-center justify-center>
        <v-tab-item>
            <v-card :loading="loading">
                <v-card-title>
                    Add or Remove Team Members
                </v-card-title>
                <v-card-subtitle class="mt-1">
                    Use the + or - buttons below to add or remove team members from your current subscription.
                </v-card-subtitle>
                <v-card-text>
                    <v-form
                        ref="updateCardholderLimitForm"
                    >
                        <v-text-field
                            :readonly="loading"
                            class="center-aligned-input"
                            :rules="noOfBusinessCardsRules"
                            outlined
                            v-model="cardholderLimit"
                            prepend-inner-icon="mdi-minus"
                            append-icon="mdi-plus"
                            @click:append="incrementCardholderLimit"
                            @click:prepend-inner="decrementCardholderLimit"
                        ></v-text-field>
                    </v-form>
                    <v-btn
                        class="mt-2 text-body-1" 
                        large 
                        elevation="0"
                        color="primary"
                        block
                        @click="updateCardholderLimit"
                    >
                        Update
                    </v-btn>
                    <v-btn
                            class="mt-2 text-body-1" 
                            large
                            text
                            elevation="0"
                            color="primary"
                            block
                            @click="$emit('canceled')"
                        >
                            Cancel
                        </v-btn>
                </v-card-text>
            </v-card>
        </v-tab-item>
        <v-tab-item>
            <v-card :loading="loading">
                <v-card-title>
                    Please Confirm
                </v-card-title>
                <v-card-subtitle>
                    Please confirm your updated subscription details below. You will be billed the new subscription amount on your next billing date.
                </v-card-subtitle>
                <v-card-text>
                    <v-list two-line subheader>
                        <v-list-item>
                            <v-list-item-avatar color="rgba( 241, 95, 49, 0.24)">
                                <v-icon
                                dark
                                >people</v-icon>
                            </v-list-item-avatar>

                            <v-list-item-content>
                                <v-list-item-subtitle>Team Members</v-list-item-subtitle>
                                <v-list-item-title class="font-weight-medium">{{ this.cardholderLimit }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item class="my-0 py-0">
                        <v-list-item-avatar color="rgba( 241, 95, 49, 0.24)">
                            <v-icon
                            dark
                            >event</v-icon>
                        </v-list-item-avatar>

                        <v-list-item-content>
                            <v-list-item-subtitle>Next Billing Date</v-list-item-subtitle>
                            <v-list-item-title class="font-weight-medium">{{ formatDate(businessProfile.nextInvoiceCreationDate.toDate()) }}</v-list-item-title>
                        </v-list-item-content>
                        </v-list-item>
                        <v-list-item class="my-0 py-0">
                        <v-list-item-avatar color="rgba( 241, 95, 49, 0.24)">
                            <v-icon
                            dark
                            >mdi-currency-usd</v-icon>
                        </v-list-item-avatar>

                        <v-list-item-content>
                            <v-list-item-subtitle>Cost Per {{ businessProfile.billingPeriod == 'yearly'? 'Year' : 'Month' }}</v-list-item-subtitle>
                            <v-list-item-title class="font-weight-medium">${{ addCommas(this.updatedBillingPrice/100) }} <sup><span class="text-caption">AUD</span></sup></v-list-item-title>
                        </v-list-item-content>
                        </v-list-item>
                    </v-list>
                    <v-btn
                        class="mt-2 text-body-1" 
                        large 
                        elevation="0"
                        color="primary"
                        block
                        @click="handleConfirmClick"
                    >
                        Confirm
                    </v-btn>
                    <v-btn
                      class="mt-2 text-body-1" 
                      large
                      text
                      elevation="0"
                      color="primary"
                      block
                      @click="$emit('canceled')"
                  >
                      Cancel Changes
                  </v-btn>
                </v-card-text>
            </v-card>
        </v-tab-item>
    </v-tabs-items>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import Utility from '@/shared/utility'
import {getHttpsCallable} from "@/shared/firebase_utils";

export default {
    name: 'UpdateCardholderLimitCard',
    data() {
        return {
            cardholderLimit: 0,
            tab: 0,
            updatedBillingPrice: 0,
            loading: false,
        }
    },
    props: {
        initialize: {
            type: Boolean,
            default: false,
        },
    },
    created() {
        this.addCommas = Utility.addCommas;
        this.removeCommas = Utility.removeCommas;
        this.formatDate = Utility.formatDate;
        this.initializeComponent();
    },
    computed: {
        ...mapGetters(['businessProfile', 'noOfCardHolders']),
        cardholderLimitWitoutCommas() {
            return parseInt(this.removeCommas(this.cardholderLimit));
        },
        noOfBusinessCardsRules() {
            return [
                v => !!v || 'Please enter the total number of team members',
                value => this.removeCommas(value) > 0 || 'Needs to be a number greater than 0',
                value => this.removeCommas(value) >= this.noOfCardHolders || `Cannot be less than the number of active team members (${this.noOfCardHolders})`,
            ];
        },
    },
    methods: {
        ...mapActions(['updateBusinessProfileDetails']),
        initializeComponent() {
            this.loading = false;
            this.tab = 0;
            this.cardholderLimit = this.addCommas(this.businessProfile.noOfEmployees) || 0;
        },
        incrementCardholderLimit() {
            this.cardholderLimit = this.addCommas(this.cardholderLimitWitoutCommas + 1);
        },
        decrementCardholderLimit() {
            if(this.cardholderLimitWitoutCommas >= 2)
                this.cardholderLimit = this.addCommas(this.cardholderLimitWitoutCommas - 1);
        },
        async updateCardholderLimit() {
            if(this.loading)
                return;
            if(this.$refs.updateCardholderLimitForm.validate()) {
                try{
                    this.loading = true;
                    const getInvoicePreview = getHttpsCallable('previewInvoiveWithUpdatedQuantity');
                    const response = await getInvoicePreview({ 
                        subscriptionId: this.businessProfile.subscriptionId,
                        quantity: this.cardholderLimit,
                        stripeCustomerId: this.businessProfile.stripeCustomerId

                    });
                    const invoicePreview  = response.data;
                    console.log(invoicePreview);
                    if (invoicePreview.amount_due != null) {
                        this.updatedBillingPrice = invoicePreview.amount_due;
                    }
                    this.tab = 1;
                    this.loading = false;
                } catch(error) {
                    this.loading = false;
                    console.log(error);
                }
            }
        },
        async handleConfirmClick() {
            if(this.loading)
                return;
            try{
                    this.loading = true;
                    const updatedQuantityInSubscription = getHttpsCallable('updatedQuantityInSubscription');
                    const response = await updatedQuantityInSubscription({ 
                        subscriptionId: this.businessProfile.subscriptionId,
                        quantity: this.cardholderLimit,
                    });
                    if (response.data.error != undefined) {
                        console.log('Error while calling the updatedQuantityInSubscription cloud function');
                        console.log(response.data.error);
                    }
                    else {
                        const subscription  = response.data;
                        if (subscription.status === 'active' || subscription.status === 'trialing' || subscription.status === 'past_due') {
                            //Update the Business Profile
                            console.log(subscription);
                            await this.updateBusinessProfileDetails({
                                subscriptionStatus: subscription.status,
                                nextInvoiceAmount: this.updatedBillingPrice,
                                noOfEmployees: this.cardholderLimit,
                            });
                        }
                    }
                    this.loading = false;
                    this.$emit('canceled');
                } catch(error) {
                    this.loading = false;
                    console.log(error);
                }
        }
    },
    watch: {
        cardholderLimit: function (newValue) {
            const result = newValue.replace(/\D/g, "")
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            this.cardholderLimit = result;
        },
        initialize: function(newVal) {
            if (newVal)
                this.initializeComponent();
        }
    },
}
</script>
<style scoped>
/deep/ .center-aligned-input input {
  text-align: center;
}
</style>